import { useMemo, useState } from 'react'
import { useReactTable, getCoreRowModel, type ColumnDef } from '@tanstack/react-table'

import { Link as InertiaLink } from '@inertiajs/react'
import { Link } from '@atoms'
import Alert from '@components/atoms/Alert'
import { DataTableRoot, DataTableContent } from '@components/molecules/DataTable'
import { PageLayout } from '@components/layouts/PageLayout'
import EmptyState from '@components/molecules/EmptyState'
import LoadingWrapper from '@components/molecules/LoadingWrapper'
import { PageHeaderNav, type PageTabs } from '@molecules/PageHeaderNav'
import {
  CallsGroupFilter,
  ShowAllGroupsLink,
  selectedCallsGroup,
  showCallsGroups,
  type CallsGroup,
} from '@components/calls/CallsGroupFilter'
import {
  GroupPageHeader,
  type GroupPageHeaderProps,
} from '@frontend/contexts/supporters-management/features/group-page/GroupPageHeader'
import { scopedTranslation } from '@utils/I18n'
import { CallListActions, type CallListActionsProps } from './CallListActions'

const t = scopedTranslation('views.call_lists.index')
const tGroupFilter = scopedTranslation('shared.group_filter')
const tSections = scopedTranslation('group_show.sections')

type CallList = CallListActionsProps['callList'] & {
  groupName: string
  createdBy: string | null
  supportersCount: number
}

export type CallListsIndexProps = {
  groupHeader: GroupPageHeaderProps | null
  pageTabs?: PageTabs | null
  groups?: CallsGroup[]
  filterGroupIds?: string[]
  callLists: CallList[]
  permissions: { canCreateCallLists: boolean; canCall: boolean; needsMobileToCall: boolean; canSendSmsBlasts: boolean }
  routes: { newCallListRoute: string; accountRoute: string }
}

const header = (label: string) => <span className="tw:font-bold">{label}</span>

const groupColumn: ColumnDef<CallList> = {
  accessorKey: 'groupName',
  header: () => header(tGroupFilter('group')),
}

const buildColumns = (
  actionProps: Omit<CallListActionsProps, 'callList'>,
  showGroup: boolean
): Array<ColumnDef<CallList>> => [
  {
    accessorKey: 'name',
    header: () => header(t('table.headers.name')),
    cell: ({ row }) => (
      <div>
        <Link href={row.original.href} rank="link" linkBackground={false} className="tw:p-0">
          {row.original.name}
        </Link>
        {row.original.sublistsCount > 0 && (
          <div className="tw:text-sm tw:text-gray-500">{t('sublists', { count: row.original.sublistsCount })}</div>
        )}
      </div>
    ),
  },
  ...(showGroup ? [groupColumn] : []),
  {
    accessorKey: 'createdBy',
    header: () => header(t('table.headers.created_by')),
  },
  {
    accessorKey: 'supportersCount',
    header: () => header(t('table.headers.supporters')),
    cell: ({ row }) =>
      row.original.supportersCount === 0 ? (
        <Link
          href={row.original.addSupportersHref}
          rank="link"
          linkBackground={false}
          className="tw:p-0"
          data-modal="true"
        >
          {t('add_supporters')}
        </Link>
      ) : (
        <span className="tw:tabular-nums">{row.original.supportersCount.toLocaleString()}</span>
      ),
    meta: { alignRight: true },
  },
  {
    id: 'actions',
    header: () => header(t('table.headers.actions')),
    cell: ({ row }) => <CallListActions callList={row.original} {...actionProps} />,
    meta: { alignRight: true },
  },
]

const CallListsIndex = ({
  groupHeader,
  pageTabs,
  groups = [],
  filterGroupIds = [],
  callLists,
  permissions,
  routes,
}: CallListsIndexProps) => {
  const [removedHrefs, setRemovedHrefs] = useState<string[]>([])
  const [filtering, setFiltering] = useState(false)
  const showGroups = !groupHeader && showCallsGroups(groups, filterGroupIds)
  const filtered = filterGroupIds.length > 0
  const selectedGroup = selectedCallsGroup(groups, filterGroupIds)
  const rows = useMemo(
    () => callLists.filter((callList) => !removedHrefs.includes(callList.href)),
    [callLists, removedHrefs]
  )
  const table = useReactTable({
    data: rows,
    columns: buildColumns(
      {
        canCall: permissions.canCall,
        canSendSmsBlasts: permissions.canSendSmsBlasts,
        onRemove: (href) => setRemovedHrefs((hrefs) => [...hrefs, href]),
        onRemoveFinished: (href) => setRemovedHrefs((hrefs) => hrefs.filter((removed) => removed !== href)),
      },
      showGroups
    ),
    getCoreRowModel: getCoreRowModel(),
  })

  const addCallListLink = permissions.canCreateCallLists && (
    <Link href={routes.newCallListRoute} trailingIcon="plus-circle" data-modal="true">
      {t('add_call_list')}
    </Link>
  )

  return (
    <PageLayout.Root>
      {groupHeader ? (
        <GroupPageHeader {...groupHeader} linkComponent={InertiaLink} />
      ) : (
        <PageLayout.Header
          icon="phone-call-01"
          heading={pageTabs?.label ?? tSections('calls')}
          nav={
            pageTabs && <PageHeaderNav ariaLabel={pageTabs.label} items={pageTabs.items} linkComponent={InertiaLink} />
          }
          actions={addCallListLink}
        />
      )}
      <PageLayout.Body>
        {showGroups && <CallsGroupFilter groups={groups} groupIds={filterGroupIds} onLoadingChange={setFiltering} />}
        {permissions.needsMobileToCall && (
          <Alert type="info" size="sm" className="tw:mb-4 tw:text-sm">
            <span>
              {t('needs_mobile.text')}{' '}
              <a href={routes.accountRoute} className="tw:underline">
                {t('needs_mobile.link')}
              </a>
            </span>
          </Alert>
        )}
        <LoadingWrapper loading={filtering}>
          {rows.length === 0 ? (
            <div className="tw:rounded-md tw:border">
              <EmptyState
                headline={
                  selectedGroup
                    ? t('empty_state.no_matches_in', { group: selectedGroup.name })
                    : t(filtered ? 'empty_state.no_matches' : 'empty_state.title')
                }
                subtext={groupHeader ? t('empty_state.description') : t('empty_state.home_description')}
                iconType="phone-call-01"
              >
                {addCallListLink}
                {filtered && <ShowAllGroupsLink />}
              </EmptyState>
            </div>
          ) : (
            <DataTableRoot table={table}>
              <DataTableContent />
            </DataTableRoot>
          )}
        </LoadingWrapper>
      </PageLayout.Body>
    </PageLayout.Root>
  )
}

export default CallListsIndex
